import { AsyncPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  EBookingPageType,
  ETimeNumericFormat,
  LayoutService,
  NotificationService,
  ResponsiveService,
  dateTimeHelper,
  ecNoOnlyWhiteSpacesValidator,
  getContrastYIQ,
} from '@cf/core';
import {
  FormFieldComponent,
  InputDirective,
  InputErrorComponent,
  LabelComponent,
} from '@cf/shared/forms/form-field';
import { ButtonComponent } from '@cf/shared/ui/button';
import { AvatarComponent, MainLogoComponent } from '@cf/shared/ui/logos';
import { DurationPipe, TextComponent, TextTruncateComponent } from '@cf/shared/ui/typography';
import {
  AppEventApiService,
  EventStatusEnum,
  IBooking,
  IBookingMember,
  IEventDetails,
  TimeFormatEnum,
} from '@cf/temp/services';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { mergeMap, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BookingApiService } from '../../services/booking-api.service';
import { BookingInformationComponent } from '../../shared/booking-information/booking-information.component';
import { ParticipantListComponent } from '../../shared/participant-list/participant-list.component';

@Component({
  selector: 'cf-cancel-booking',
  templateUrl: './cancel-booking-page.component.html',
  styleUrls: ['./cancel-booking-page.component.scss', '../booking/booking-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MainLogoComponent,
    AvatarComponent,
    NgIf,
    TextComponent,
    TextTruncateComponent,
    DurationPipe,
    NgxTolgeeModule,
    BookingInformationComponent,
    ParticipantListComponent,
    LabelComponent,
    FormFieldComponent,
    InputDirective,
    ReactiveFormsModule,
    InputErrorComponent,
    ButtonComponent,
    RouterLink,
    AsyncPipe,
  ],
})
export class CancelBookingPageComponent implements OnInit, OnDestroy {
  token!: string;

  event!: IEventDetails & IBooking;
  participants!: IBookingMember[];

  isTouch$ = this.responsiveService.isTouch$;

  reasonControl: FormControl<string> = new FormControl('', [
    Validators.required,
    ecNoOnlyWhiteSpacesValidator,
    Validators.maxLength(500),
  ]) as any;

  readonly EMeetingType = EBookingPageType;
  readonly getContrastYIQ = getContrastYIQ;

  constructor(
    private readonly responsiveService: ResponsiveService,
    private readonly eventApiService: AppEventApiService,
    private readonly bookingApiService: BookingApiService,
    private readonly notificationService: NotificationService,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    readonly layoutService: LayoutService,
    private readonly translateService: TranslateService,
  ) {
    layoutService.hideHeader.next(true);
  }

  get timeFormat(): ETimeNumericFormat {
    return this.event.time_format === TimeFormatEnum['12h']
      ? ETimeNumericFormat.TIME_12
      : ETimeNumericFormat.TIME_24;
  }

  get time() {
    return dateTimeHelper.getTime(this.event.start, this.timeFormat, false, this.event.timezone);
  }

  get rejected() {
    return this.event?.status === EventStatusEnum.REJECTED;
  }

  ngOnInit() {
    this.checkToken();
  }

  ngOnDestroy() {
    this.layoutService.hideHeader.next(false);
  }

  submit() {
    this.eventApiService.cancelEvent(this.token, this.reasonControl.value).subscribe(() => {
      void this.router.navigate(['/']);
    });
  }

  private checkToken(): void {
    const cancelEventToken = this.route.snapshot.queryParams.cancelEventToken;
    if (cancelEventToken) {
      this.token = cancelEventToken;
      this.getData();
      return;
    }
  }

  private getData(): void {
    this.eventApiService
      .event(this.token)
      .pipe(
        mergeMap((event) => {
          if (!event.meeting_template?.path) {
            return of(event as any);
          }
          return this.bookingApiService
            .booking(event.meeting_template.path, event.meeting_template.prefix!)
            .pipe(
              map((res) => ({
                ...res,
                ...event,
              })),
            );
        }),
      )
      .subscribe((res) => {
        if (res?.is_cancelled) {
          this.notificationService.error(this.translateService.instant('events.already_canceled'));
          void this.router.navigate(['/']);
        }
        this.event = res;
        this.participants = this.event.attendees.map((a) => ({
          name: a.attendee?.name ?? a.user?.name ?? '',
          email: a.attendee?.email ?? a.user?.email ?? '',
          role: a.role,
        }));
        this.cd.detectChanges();
      });
  }
}
